import type { ClientProductEventInput } from '@scoutaround/script-breakdown-shared';

import { getToken } from './api.js';

/**
 * The workspace's product-event beacon (BI audit gap 25).
 *
 * Events queue per project and leave every 5 s, when a project's queue reaches a
 * full batch, and when the tab is hidden or closed (`installFlushOnHide`).
 *
 * `fetch(…, { keepalive: true })` and not `sendBeacon`: the API needs the bearer,
 * and a beacon cannot carry a header. Two limits shape a request:
 *
 * - the server takes at most 50 events per batch (`ProductEventBatch`);
 * - a browser refuses a `keepalive` body over 64 KiB (the quota is shared by every
 *   keepalive request in flight). A batch is therefore also cut by its serialized
 *   size, kept under `TELEMETRY_MAX_BODY_BYTES`.
 *
 * Every request of a flush is dispatched synchronously, not one after another:
 * on `pagehide` there is no later tick in which an awaited second request would
 * be sent.
 *
 * NOTHING HERE EVER THROWS INTO A SCREEN. A lost product event is a lost row.
 */
export const TELEMETRY_FLUSH_MS = 5000;
export const TELEMETRY_MAX_BODY_BYTES = 60_000;
const MAX_BATCH = 50;
const SESSION_KEY = 'sb.sid';

const queues = new Map<string, ClientProductEventInput[]>();
let timer: ReturnType<typeof setTimeout> | null = null;
let fallbackSession: string | null = null;

/**
 * A v4 UUID. `crypto.randomUUID` exists only in a secure context, and a dev
 * server reached over plain http on a LAN address is not one; `getRandomValues`
 * is available everywhere.
 */
function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** One id per browser tab: sessionStorage survives a reload and is never shared between tabs. */
export function sessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const minted = uuid();
    sessionStorage.setItem(SESSION_KEY, minted);
    return minted;
  } catch {
    // Private windows and blocked site data throw; the id then lives as long as the page.
    fallbackSession ??= uuid();
    return fallbackSession;
  }
}

export function track(
  projectId: string,
  eventName: ClientProductEventInput['eventName'],
  props: ClientProductEventInput['props'] = {},
  subject?: { type: string; id: string },
): void {
  try {
    const event: ClientProductEventInput = {
      eventId: uuid(),
      eventName,
      clientOccurredAt: new Date().toISOString(),
      sessionId: sessionId(),
      pagePath: window.location.pathname.slice(0, 512),
      ...(subject === undefined ? {} : { subjectType: subject.type, subjectId: subject.id }),
      props,
    };

    const queue = queues.get(projectId) ?? [];
    queue.push(event);
    queues.set(projectId, queue);

    if (queue.length >= MAX_BATCH) {
      void flush();
      return;
    }
    timer ??= setTimeout(() => {
      timer = null;
      void flush();
    }, TELEMETRY_FLUSH_MS);
  } catch {
    // Telemetry is never the reason a screen breaks.
  }
}

const encoder = new TextEncoder();
const byteLength = (text: string): number => encoder.encode(text).length;

/**
 * Cuts one project's events into request bodies of at most 50 events and under
 * the byte budget, in order. An event too large to fit any body alone still
 * goes alone — the browser may refuse it, which `send` swallows.
 */
function bodies(events: readonly ClientProductEventInput[]): string[] {
  const out: string[] = [];
  let parts: string[] = [];
  // `{"events":[` + `]}`; one comma between each pair of parts.
  const envelope = byteLength('{"events":[]}');
  let size = envelope;

  for (const event of events) {
    const part = JSON.stringify(event);
    const partBytes = byteLength(part);
    if (parts.length > 0 && (parts.length >= MAX_BATCH || size + 1 + partBytes >= TELEMETRY_MAX_BODY_BYTES)) {
      out.push(`{"events":[${parts.join(',')}]}`);
      parts = [];
      size = envelope;
    }
    size += partBytes + (parts.length === 0 ? 0 : 1);
    parts.push(part);
  }
  if (parts.length > 0) out.push(`{"events":[${parts.join(',')}]}`);
  return out;
}

function send(url: string, token: string, body: string): Promise<unknown> {
  try {
    return fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body,
    }).catch(() => undefined);
  } catch {
    return Promise.resolve();
  }
}

export async function flush(): Promise<void> {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  const pending = [...queues.entries()];
  queues.clear();

  // Signed out: there is nobody to attribute the events to, and the API would
  // answer 401. They are dropped rather than held for a different person.
  const token = getToken();
  if (token === null || pending.length === 0) return;

  const requests: Promise<unknown>[] = [];
  for (const [projectId, events] of pending) {
    const url = `/api/v1/projects/${encodeURIComponent(projectId)}/events`;
    try {
      for (const body of bodies(events)) requests.push(send(url, token, body));
    } catch {
      // A lost product event is a lost row, never an error on screen.
    }
  }
  await Promise.allSettled(requests);
}

/** Flush when the tab is hidden or closed. Returns the uninstaller. */
export function installFlushOnHide(): () => void {
  const onHidden = (): void => {
    if (document.visibilityState === 'hidden') void flush();
  };
  const onPageHide = (): void => {
    void flush();
  };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
  };
}
